/* eslint-disable react-hooks/immutability -- ReactiveProperty.value is a setter that notifies subscribers, not a plain field mutation */
/**
 * Point cloud property integration — UI components only.
 *
 * Property registration and the apply path live in:
 *   - libs/potree-shared/src/integration/pointcloud/PointCloudPropertyGetters.ts (registration + getters)
 *   - apps/client/src/potree/PointCloudOctree.ts#bindReactiveProperties (subscriptions)
 *
 * UI handlers here only write `property.value = newValue`. The PointCloudOctree subscriptions
 * apply the change to material / classifications / sources.
 */

import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import { memo, useEffect, useMemo, useState, useContext, useCallback } from 'react';
import { propertyRegistry, PropertyInput, PropertyInstance, PropertyValueData } from '../index';
import { Option, Select, SelectProps } from '../../../components/inputs/Select';
import { RendererContext } from '../../../contexts/RendererContext';
import { useFormatNumber, ConvertType } from '../../../hooks/useFormatNumber';
import { useClassificationTranslations } from '../../../hooks/translations/useClassifications';
import { useClassifications } from '../../../hooks/potree/useRenderer';
import useOpen from '../../../hooks/useOpen';
import { IClassification } from '../../../hooks/potree/useRenderer';
import { ClassificationSettingsModal } from '../../user/ClassificationSettingsModal';
import { PropertyConfig } from '../types';
import {
  PointCloudPropertyKeys,
  getPointCloudGradientProperty,
  getPointCloudPointSizeProperty,
  getPointCloudAppearanceProperty,
  getPointCloudResolutionProperty,
  getPointCloudHeightRangeProperty,
  getPointCloudIntensityRangeProperty,
  getPointCloudHiddenClassificationsProperty,
  getPointCloudHiddenSourcesProperty,
  getViewerEdlEnabledProperty,
  hasGlobalPropertyService,
} from '@pointorama/potree-shared';
import type { ReactiveProperty, Subscription } from '@pointorama/potree-shared';

export { PointCloudPropertyKeys };

// ============================================================================
// HOOK: subscribe to a ReactiveProperty and re-render on change
// ============================================================================

function useReactivePropertyValue<T extends PropertyValueData>(property: ReactiveProperty<T> | null): T | undefined {
  const [value, setValue] = useState<T | undefined>(() => property?.value);

  useEffect(() => {
    if (!property) return;
    const sub: Subscription = property.subscribe((v) => setValue(v));
    return () => sub.unsubscribe();
  }, [property]);

  return value;
}

function makeInstance(config: PropertyConfig | undefined, value: PropertyValueData, objectIdentifier: string): PropertyInstance | null {
  if (!config) return null;
  return { config, value, objectIdentifier } as PropertyInstance;
}

interface PointCloudPropertyProps {
  pointCloudId: string;
}

// ============================================================================
// PROPERTY COMPONENTS
// ============================================================================

const PointCloudGradientProperty_: React.FC<PointCloudPropertyProps> = ({ pointCloudId }) => {
  const property = hasGlobalPropertyService() ? getPointCloudGradientProperty(pointCloudId) : null;
  const value = useReactivePropertyValue(property);
  const config = propertyRegistry.getConfig(PointCloudPropertyKeys.HEIGHT_GRADIENT);
  const instance = makeInstance(config, value ?? 'SPECTRAL', pointCloudId);
  if (!instance || !property) return null;

  return (
    <div className="flex items-center w-full">
      <PropertyInput instance={instance} onChange={(next) => (property.value = next as string)} />
    </div>
  );
};
export const PointCloudGradientProperty = memo(PointCloudGradientProperty_);

const PointCloudPointSizeProperty_: React.FC<PointCloudPropertyProps> = ({ pointCloudId }) => {
  const property = hasGlobalPropertyService() ? getPointCloudPointSizeProperty(pointCloudId) : null;
  const value = useReactivePropertyValue(property);
  const config = propertyRegistry.getConfig(PointCloudPropertyKeys.POINT_SIZE);
  const instance = makeInstance(config, value ?? 1.0, pointCloudId);
  if (!instance || !property) return null;

  return <PropertyInput instance={instance} onChange={(next) => (property.value = next as number)} className="w-2/3" />;
};
export const PointCloudPointSizeProperty = memo(PointCloudPointSizeProperty_);

/**
 * EDL toggle — viewer-scoped (PROJECT_USER, objectIdentifier='viewer'), not per-pointcloud.
 * The `pointCloudId` prop is accepted for API compatibility with sibling components but ignored.
 */
const PointCloudEdgesProperty_: React.FC<PointCloudPropertyProps> = () => {
  const property = hasGlobalPropertyService() ? getViewerEdlEnabledProperty() : null;
  const value = useReactivePropertyValue(property);
  const config = propertyRegistry.getConfig(PointCloudPropertyKeys.VIEWER_EDL_ENABLED);
  const instance = makeInstance(config, value ?? true, 'viewer');
  if (!instance || !property) return null;

  return <PropertyInput instance={instance} onChange={(next) => (property.value = next as boolean)} />;
};
export const PointCloudEdgesProperty = memo(PointCloudEdgesProperty_);

const PointCloudAppearanceProperty_: React.FC<PointCloudPropertyProps> = ({ pointCloudId }) => {
  const rendererContext = useContext(RendererContext);
  const selectedPointCloud = rendererContext.viewer?.scene.pointclouds.find((pc) => pc.identifier === pointCloudId);

  const property = hasGlobalPropertyService() ? getPointCloudAppearanceProperty(pointCloudId) : null;
  const reactiveValue = useReactivePropertyValue(property);
  const value = reactiveValue !== undefined ? reactiveValue : selectedPointCloud?.material?.activeAttributeName || 'rgba';
  const config = propertyRegistry.getConfig(PointCloudPropertyKeys.APPEARANCE);
  const instance = makeInstance(config, value, pointCloudId);
  if (!instance || !property) return null;

  return <PropertyInput instance={instance} onChange={(next) => (property.value = next as string)} className="w-full" />;
};
export const PointCloudAppearanceProperty = memo(PointCloudAppearanceProperty_);

const PointCloudHeightRangeProperty_: React.FC<PointCloudPropertyProps> = ({ pointCloudId }) => {
  const rendererContext = useContext(RendererContext);
  const selectedPointCloud = rendererContext.viewer?.scene.pointclouds.find((pc) => pc.identifier === pointCloudId);

  const initialMin = selectedPointCloud?.material?.initialHeightMin;
  const initialMax = selectedPointCloud?.material?.initialHeightMax;
  const boundsAvailable = initialMin !== undefined && initialMax !== undefined;

  const property = boundsAvailable && hasGlobalPropertyService() ? getPointCloudHeightRangeProperty(pointCloudId, initialMin, initialMax) : null;
  const reactiveValue = useReactivePropertyValue(property);

  if (!boundsAvailable || !property) return null;

  const value = reactiveValue !== undefined ? reactiveValue : ([selectedPointCloud?.material?.heightMin ?? initialMin, selectedPointCloud?.material?.heightMax ?? initialMax] as [number, number]);
  const config = propertyRegistry.getConfig(PointCloudPropertyKeys.HEIGHT_RANGE);
  if (!config) return null;

  // Override registry config with this pointcloud's actual bounds so reset/validation use the right range
  const instanceConfig = { ...config, defaultValue: [initialMin, initialMax], validation: { ...config.validation, min: initialMin, max: initialMax } };
  const instance = { config: instanceConfig, value, objectIdentifier: pointCloudId } as PropertyInstance;

  return <PropertyInput instance={instance} onChange={(next) => (property.value = next as [number, number])} className="w-full" />;
};
export const PointCloudHeightRangeProperty = memo(PointCloudHeightRangeProperty_);

const PointCloudIntensityRangeProperty_: React.FC<PointCloudPropertyProps> = ({ pointCloudId }) => {
  const rendererContext = useContext(RendererContext);
  const selectedPointCloud = rendererContext.viewer?.scene.pointclouds.find((pc) => pc.identifier === pointCloudId);

  const rawInitialMin = selectedPointCloud?.material?.initialIntensityMin;
  const rawInitialMax = selectedPointCloud?.material?.initialIntensityMax;
  const initialMin = rawInitialMin !== undefined ? Math.round(rawInitialMin) : undefined;
  const initialMax = rawInitialMax !== undefined ? Math.round(rawInitialMax) : undefined;
  const boundsAvailable = initialMin !== undefined && initialMax !== undefined;

  const property = boundsAvailable && hasGlobalPropertyService() ? getPointCloudIntensityRangeProperty(pointCloudId, initialMin, initialMax) : null;
  const reactiveValue = useReactivePropertyValue(property);

  if (!boundsAvailable || !property) return null;

  const value = reactiveValue !== undefined ? reactiveValue : ([Math.round(selectedPointCloud?.material?.intensityMin ?? initialMin), Math.round(selectedPointCloud?.material?.intensityMax ?? initialMax)] as [number, number]);
  const config = propertyRegistry.getConfig(PointCloudPropertyKeys.INTENSITY_RANGE);
  if (!config) return null;

  const instanceConfig = { ...config, defaultValue: [initialMin, initialMax], validation: { ...config.validation, min: initialMin, max: initialMax } };
  const instance = { config: instanceConfig, value, objectIdentifier: pointCloudId } as PropertyInstance;

  return <PropertyInput instance={instance} onChange={(next) => (property.value = next as [number, number])} className="w-full" />;
};
export const PointCloudIntensityRangeProperty = memo(PointCloudIntensityRangeProperty_);

const PointCloudResolutionProperty_: React.FC<PointCloudPropertyProps> = ({ pointCloudId }) => {
  const rendererContext = useContext(RendererContext);
  const selectedPointCloud = rendererContext.viewer?.scene.pointclouds.find((pc) => pc.identifier === pointCloudId);
  const { formatNumber } = useFormatNumber();

  const resolutionLevels = useMemo(() => {
    const depth = selectedPointCloud?.pcoGeometry?.depth || 21;
    return Array.from({ length: depth + 1 - 7 }, (_, index) => index).reverse();
  }, [selectedPointCloud?.pcoGeometry?.depth]);

  const resolutionOptions = useMemo(() => {
    const scale = selectedPointCloud?.pcoGeometry?.scale || 1;
    return resolutionLevels.map((level) => {
      const computedValue = Math.pow(2, 14 - level) / scale;
      const formattedValue = formatNumber(computedValue, { addUnit: true, convertType: ConvertType.DISTANCE });
      const label = level === resolutionLevels[0] ? 'all points' : formattedValue;
      return { value: level, label };
    });
  }, [resolutionLevels, selectedPointCloud?.pcoGeometry?.scale, formatNumber]);

  const property = hasGlobalPropertyService() ? getPointCloudResolutionProperty(pointCloudId) : null;
  const reactiveValue = useReactivePropertyValue(property);
  const savedValue = reactiveValue !== undefined && reactiveValue !== -1 ? reactiveValue : undefined;
  const value = savedValue ?? (selectedPointCloud?.downSamplingLevel !== undefined && selectedPointCloud?.downSamplingLevel !== Infinity ? selectedPointCloud.downSamplingLevel : resolutionLevels[0]);
  const config = propertyRegistry.getConfig(PointCloudPropertyKeys.RESOLUTION);
  if (!config || !property) return null;

  const instanceConfig = { ...config, validation: { ...config.validation, options: resolutionOptions } };
  const instance = { config: instanceConfig, value, objectIdentifier: pointCloudId } as PropertyInstance;

  return (
    <PropertyInput
      instance={instance}
      onChange={(next) => {
        property.value = (next === null || next === undefined ? Infinity : next) as number;
      }}
      className="w-full"
    />
  );
};
export const PointCloudResolutionProperty = memo(PointCloudResolutionProperty_);

// ============================================================================
// CLASSIFICATION FILTER (reactive)
// ============================================================================

type PointcloudClassification = IClassification & { visible: boolean };

interface PointCloudClassificationFilterProps {
  pointCloudId: string;
  availableClasses: number[];
  projectId: string;
}

const PointCloudClassificationFilter_: React.FC<PointCloudClassificationFilterProps> = ({ pointCloudId, availableClasses, projectId }) => {
  const classificationTranslations = useClassificationTranslations();
  const [{ classifications }] = useClassifications();

  const property = hasGlobalPropertyService() ? getPointCloudHiddenClassificationsProperty(pointCloudId) : null;
  const hiddenValue = useReactivePropertyValue(property);
  const hidden = useMemo(() => (Array.isArray(hiddenValue) ? hiddenValue : []), [hiddenValue]);

  const classificationOptions = useMemo<PointcloudClassification[]>(
    () => classifications.filter((c) => availableClasses.includes(c.code)).map((c) => ({ ...c, visible: !hidden.includes(c.code) })),
    [classifications, availableClasses, hidden],
  );

  const classificationsValue = useMemo(() => classificationOptions.filter((c) => c.visible), [classificationOptions]);

  const onChangeClassification: SelectProps<PointcloudClassification[]>['onChange'] = useCallback(
    (event) => {
      if (!property) return;
      if (!event.target.value?.length) {
        // Hide every available class
        property.value = Array.from(new Set([...hidden, ...classificationOptions.map((c) => c.code)]));
        return;
      }
      const newValues = event.target.value as PointcloudClassification[];
      const newCodes = newValues.map((c) => c.code);
      const previousCodes = classificationOptions.filter((c) => c.visible).map((c) => c.code);
      const toggled = newCodes.length > previousCodes.length ? newValues.find((c) => !previousCodes.includes(c.code)) : classificationOptions.find((c) => c.visible && !newCodes.includes(c.code));
      if (!toggled) return;
      const isCurrentlyHidden = hidden.includes(toggled.code);
      property.value = isCurrentlyHidden ? hidden.filter((code) => code !== toggled.code) : [...hidden, toggled.code];
    },
    [property, hidden, classificationOptions],
  );

  const { onOpenWithValue: openClassificationSettingsModal, onClose: closeClassificationSettingsModal, open: classificationSettingsModalOpen, value: customClassification } = useOpen<PointcloudClassification>();

  return (
    <>
      <Select<PointcloudClassification[]> className="w-full" onChange={onChangeClassification} value={classificationsValue} name="classification" multiple clearable>
        {classificationOptions.map((option) => {
          const isCustomClass = option.code >= 64;
          return (
            <Option key={option.code} value={option}>
              <div className="flex">
                <div className="flex items-center grow truncate">
                  <div className="shrink-0 w-4 h-4 mr-1 border border-gray-500 rounded " style={{ backgroundColor: `rgb(${option.color.map((c: number) => c * 255).join(', ')})` }} />
                  {classificationTranslations[option.name] || option.name}
                </div>
                {isCustomClass && (
                  <div className="shrink-0 ml-2">
                    <Cog6ToothIcon
                      className="w-4 h-4 text-gray-700 hover:text-black dark:hover:text-neon-green-300"
                      onClick={(e) => {
                        e.stopPropagation();
                        openClassificationSettingsModal(option);
                      }}
                    />
                  </div>
                )}
              </div>
            </Option>
          );
        })}
      </Select>
      {customClassification && <ClassificationSettingsModal onClose={closeClassificationSettingsModal} open={classificationSettingsModalOpen} projectId={projectId} value={customClassification} />}
    </>
  );
};
export const PointCloudClassificationFilter = memo(PointCloudClassificationFilter_);

// ============================================================================
// SOURCE FILTER (reactive)
// ============================================================================

interface PointCloudSourceFilterProps {
  pointCloudId: string;
  availableSources: number[];
}

type PointcloudSource = { id: number; color: [number, number, number, number]; visible: boolean };

const PointCloudSourceFilter_: React.FC<PointCloudSourceFilterProps> = ({ pointCloudId, availableSources }) => {
  const rendererContext = useContext(RendererContext);
  const viewer = rendererContext.viewer;

  const property = hasGlobalPropertyService() ? getPointCloudHiddenSourcesProperty(pointCloudId) : null;
  const hiddenValue = useReactivePropertyValue(property);
  const hidden = useMemo(() => (Array.isArray(hiddenValue) ? hiddenValue : []), [hiddenValue]);

  const sourceOptions = useMemo<PointcloudSource[]>(() => {
    const sources = viewer?.sourcesInfo.getSourceIdsForPointCloud({ pointcloudId: pointCloudId }) ?? [];
    return sources.filter((s) => availableSources.includes(s.id)).map((s) => ({ id: s.id, color: s.color, visible: !hidden.includes(s.id) }));
  }, [viewer, pointCloudId, availableSources, hidden]);

  const sourcesValue = useMemo(() => sourceOptions.filter((s) => s.visible), [sourceOptions]);

  const onChangeSource: SelectProps<PointcloudSource[]>['onChange'] = useCallback(
    (event) => {
      if (!property) return;
      if (!event.target.value?.length) {
        property.value = Array.from(new Set([...hidden, ...sourceOptions.map((s) => s.id)]));
        return;
      }
      const newValues = event.target.value as PointcloudSource[];
      const newIds = newValues.map((s) => s.id);
      const previousIds = sourceOptions.filter((s) => s.visible).map((s) => s.id);
      const toggled = newIds.length > previousIds.length ? newValues.find((s) => !previousIds.includes(s.id)) : sourceOptions.find((s) => s.visible && !newIds.includes(s.id));
      if (!toggled) return;
      const isCurrentlyHidden = hidden.includes(toggled.id);
      property.value = isCurrentlyHidden ? hidden.filter((sid) => sid !== toggled.id) : [...hidden, toggled.id];
    },
    [property, hidden, sourceOptions],
  );

  return (
    <Select<PointcloudSource[]> className="w-full" onChange={onChangeSource} value={sourcesValue} name="source" multiple clearable>
      {sourceOptions.map((source) => (
        <Option key={source.id} value={source}>
          <div className="flex">
            <div className="flex items-center grow truncate">
              <div className="shrink-0 w-4 h-4 mr-1 border border-gray-500 rounded " style={{ backgroundColor: `rgb(${source.color.map((c: number) => c * 255).join(', ')})` }} />
              {source.id}
            </div>
          </div>
        </Option>
      ))}
    </Select>
  );
};
export const PointCloudSourceFilter = memo(PointCloudSourceFilter_);
